import { useEffect, useRef, useState } from "react";
import { Stock, ValidationResultItem } from "@/types/types";
import { useStockCreation } from "@/hooks/useStockCreation";
import { ClickEvent } from "devextreme/ui/button";
import { closePopup, reloadGrid } from "@/helper/DxHelper";
import {
  Button,
  LoadIndicator,
  Popup,
  TextBox,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import ServerResponse from "@/components/ServerResponse";
import { AxiosError } from "axios";
import { RequiredRule } from "devextreme-react/validator";
import { PopupRef } from "devextreme-react/cjs/popup";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { TextBoxRef } from "devextreme-react/cjs/text-box";

type Props = {
  popupRef: React.RefObject<PopupRef>;
  datagridRef: React.RefObject<DataGridRef<Stock, string>>;
};

const validationGroupName = "insertStockVal";

export default function Insert({ datagridRef, popupRef }: Props) {
  const [serverErrors, setServerErrors] = useState<ValidationResultItem[] | null>(null);
  const { mutate, data: response, isPending, isError, error, isSuccess } = useStockCreation();

  const nameRef = useRef<TextBoxRef>(null);
  const codeRef = useRef<TextBoxRef>(null);
  const salesforceIdRef = useRef<TextBoxRef>(null);

  function submit(e: ClickEvent) {
    setServerErrors(null);

    if (!e.validationGroup.validate().isValid) return;

    const requestData: Partial<Stock> = {
      name: nameRef?.current?.instance().option("value"),
      code: codeRef?.current?.instance().option("value"),
      salesforceID: salesforceIdRef?.current?.instance().option("value"),
    };

    mutate(requestData);
  }

  function disableInputs(state: boolean) {
    nameRef.current?.instance().option("disabled", state);
    codeRef.current?.instance().option("disabled", state);
    salesforceIdRef.current?.instance().option("disabled", state);
  }

  function clearForm() {
    nameRef.current?.instance().option("value", "");
    codeRef.current?.instance().option("value", "");
    salesforceIdRef.current?.instance().option("value", "");
  }

  useEffect(() => {
    if (isSuccess) {
      if (!response?.validation.items?.length) {
        clearForm();
        closePopup(popupRef);
      } else {
        setServerErrors(response?.validation.items);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    disableInputs(isPending);
  }, [isPending]);

  return (
    <Popup
      title="Add Stock entry"
      showCloseButton={true}
      ref={popupRef}
      onHiding={() => {
        setServerErrors(null);
        reloadGrid(datagridRef);
      }}
    >
      <form className="mx-1 grid grid-cols-1 gap-8">
        <TextBox
          label="Name*"
          ref={nameRef}
          stylingMode="outlined"
          labelMode="static"
          valueChangeEvent="keyup"
        >
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Name is required"} />
          </Validator>
        </TextBox>

        <TextBox
          ref={codeRef}
          label="Code*"
          stylingMode="outlined"
          labelMode="static"
          valueChangeEvent="keyup"
        >
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Code is required"} />
          </Validator>
        </TextBox>
        {/* <TextBox
          ref={salesforceIdRef}
          label="Salesforce ID"
          stylingMode="outlined"
          labelMode="static"
          valueChangeEvent="keyup"
        /> */}
      </form>
      <div className="absolute bottom-4 left-4">
        <div className="text-danger">
          {isError && error instanceof AxiosError ? <span>{error.message}</span> : null}
        </div>
        <ValidationSummary validationGroup={validationGroupName} id="summary"></ValidationSummary>
        {serverErrors?.length ? <ServerResponse items={serverErrors} /> : null}
      </div>
      <div className="absolute bottom-4 right-4">
        <Button
          validationGroup={validationGroupName}
          useSubmitBehavior={false}
          type="default"
          height={40}
          width={isPending ? 150 : 100}
          onClick={submit}
        >
          <LoadIndicator
            id="default-btn-loading"
            className="mr-3 inline-block align-middle"
            width={25}
            height={25}
            visible={isPending}
          />
          <span className="dx-button-text">{isPending ? "Creating" : "Create"}</span>
        </Button>
      </div>
    </Popup>
  );
}
